import { FeedContentDto, FeedItemDto, FeedContentUpdate } from "models/ApiModels";
import { Reducer } from "redux";
import { FeedActionKeys } from "./keys";
import { FeedActionTypes } from "./types";
import { deepMerge } from "lib/DeepMerge";
import { GenieActionKeys } from "store/genie/keys";
import { GenieActionTypes } from "store/genie/types";
import { forEach, findIndex } from "lodash";

//////////////////////////////////////////////////////////////////////////////////////

export interface FeedState {
    readonly data: FeedContentDto[];
    readonly totalRecords: number;
    readonly hasMore: boolean;
    readonly loading: boolean;
    readonly reseting: boolean;
    readonly lastError: string;
}

const initialState: FeedState = {
    data: [],
    totalRecords: 0,
    hasMore: true,
    reseting: false,
    loading: false,
    lastError: null,
};

//////////////////////////////////////////////////////////////////////////////////////

export function addFeedContent(data: FeedContentDto[], uid: string, items: FeedItemDto[]): FeedContentDto[] {

    const index = (uid != null) ? data.findIndex(p => p.uid == uid) : null;
    if (index === -1) return [{ uid, items }].concat(data);

    return data;
}


//////////////////////////////////////////////////////////////////////////////////////

export function updateFeedContent(data: FeedContentDto[], uid: string, items: DeepPartial<FeedItemDto>[], replace: boolean = false): FeedContentDto[] {

    const index = (uid != null) ? data.findIndex(p => p.uid == uid) : null;
    if (index === -1) return data;

    return data.map((content, i) => {
        if (index == null || i === index) {
            items.forEach((item) => {
                const index2 = content.items.findIndex(p => p.publicationId == item.publicationId);
                if (index2 > -1) {
                    if (replace) {
                        content.items[index2] = item as FeedItemDto;
                    }
                    else {
                        content.items[index2] = deepMerge(content.items[index2], item, { arrayMerge: (target: any, source: any, options: any) => source });
                    }
                }
            });
        }
        return content;
    });
}

export function updateFeedContentArray(data: FeedContentDto[], newItems: FeedContentUpdate[], replace: boolean = false): FeedContentDto[] {

    return data.map((sourceItem) => {

        const newItem = newItems.find(p => p.uid == sourceItem.uid);
        if (newItem) {

            for (var i = 0; i < newItem.items.length; i++) {

                var sPub = sourceItem.items.find(x => x.publicationId == newItem.items[i].publicationId);
                if (sPub) {
                    if (replace) {
                        sPub = newItem.items[i] as FeedItemDto;
                    }
                    else {
                        sPub = deepMerge(sPub, newItem.items[i], { arrayMerge: (target: any, source: any, options: any) => source });
                    }
                }
            }
        }

        return sourceItem;
    });
}

//////////////////////////////////////////////////////////////////////////////////////



export function deleteFeedContent(data: FeedContentDto[], uid: string, publicationId: string): FeedContentDto[] {

    const index = (uid != null) ? data.findIndex(p => p.uid == uid) : null;
    if (index === -1) return data;

    const newData: FeedContentDto[] = [];

    data.forEach((content, i) => {
        if (index == null || i === index) {
            const index2 = content.items.findIndex(p => p.publicationId == publicationId);
            if (index2 > -1) content.items = content.items.slice(index2, index2);
        }
        if (content.items.length > 0)
            newData.push(content);
    });

    return newData;
}

export function existsFeedContent(data: FeedContentDto[], publicationId: string): boolean {

    for (var i = 0; i < data.length; i++) {
        const content = data[i];
        const index2 = content.items.findIndex(p => p.publicationId == publicationId);
        if (index2 > -1) return true;
    };

    return false;
}

//////////////////////////////////////////////////////////////////////////////////////

export function feedReset(): FeedState {
    return { data: [], totalRecords: 0, reseting: true, loading: false, hasMore: false, lastError: null };
}
//////////////////////////////////////////////////////////////////////////////////////


const feedReducer: Reducer<FeedState, FeedActionTypes> = (state = initialState, action: FeedActionTypes | GenieActionTypes) => {

    switch (action.type) {
        case GenieActionKeys.INIT:
            return feedReset();

        case FeedActionKeys.FEED_RESET:
            return feedReset();

        case FeedActionKeys.FEED_GET_REQUEST:
            return {
                ...state,
                reseting: false,
                loading: true,
                lastError: null
            };

        case FeedActionKeys.FEED_GET_FAILURE:
            return {
                ...state,
                reseting: false,
                loading: false,
                hasMore: false,
                lastError: action.payload
            };

        case FeedActionKeys.FEED_GET_SUCCESS:
            return {
                ...state,
                reseting: false,
                loading: false,
                lastError: null,
                totalRecords: action.payload.totalRecords,
                hasMore: action.payload.hasMore,
                data: [...state.data, ...action.payload.content]
            };

        case FeedActionKeys.FEED_ADD_DATA: {
            if (action.payload && action.payload) {
                const newData = addFeedContent(state.data, action.payload.uid, action.payload.items);
                const newTotal = (action.payload.uid != null && state.totalRecords != -1) ? state.totalRecords + 1 : state.totalRecords;

                return {
                    ...state,
                    ...{
                        data: newData,
                        totalRecords: newTotal
                    }
                };
            }
            return state;
        }

        case FeedActionKeys.FEED_REPLACE_DATA: {
            if (action.payload && action.payload.uid) {
                return {
                    ...state,
                    ...{ data: updateFeedContent(state.data, action.payload.uid, action.payload.items, true) }
                };
            }
            return state;
        }


        case FeedActionKeys.FEED_UPDATE_DATA: {
            if (action.payload) {
                const newData = Array.isArray(action.payload)
                    ? updateFeedContentArray(state.data, action.payload)
                    : updateFeedContent(state.data, action.payload.uid, action.payload.items);

                return {
                    ...state,
                    ...{ data: newData}
                };
            }
            return state;
        }

        case FeedActionKeys.FEED_DELETE_DATA: {
            const newContent = deleteFeedContent(state.data, action.payload.uid, action.payload.publicationId);
            const newTotal = (action.payload.uid != null) ? state.totalRecords - 1 : state.totalRecords;

            if (action.payload && action.payload.uid) {
                return {
                    ...state,
                    ...{ data: newContent },
                    totalRecords: newTotal
                };
            }
            return state;
        }

        case FeedActionKeys.FEED_EXECUTING_ACTION: {
            if (action.payload && action.payload.uid) {
                return {
                    ...state,
                    ...{
                        data: updateFeedContent(state.data, action.payload.uid,
                            [
                                {
                                    publicationId: action.payload.publicationId,
                                    isUpdating: action.payload.running
                                }
                            ])
                    }
                };
            }
            return state;
        }

        case FeedActionKeys.FEED_TRY_UPDATE_PUBLICATION: {
            return {
                ...state,
                ...{ data: updateFeedContent(state.data, null, [action.payload]) }
            };
        }

        case FeedActionKeys.FEED_TRY_DELETE_PUBLICATION: {
            if (existsFeedContent(state.data, action.payload.publicationId))
                return feedReset();

            return state;
        }

        default:
            return state;
    }
};

export { feedReducer };